import React, { useState } from 'react'
import moment from 'moment'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faFileUpload } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import { getMyProviderProfile, updateProviderDocuments } from '../../../helpers/api/marketplace'
import { DOCUMENTS, formatIdentification } from '../marketplaceConstants'
import ProviderStatusCard from './ProviderStatusCard'
import ProviderDocumentsFields from './ProviderDocumentsFields'

const formatPhone = (phone) => (phone ? formatPhoneNumberIntl(phone) || phone : phone)

const DataRow = ({ label, children }) => (
  <Row className="mb-2 fs--1">
    <Col sm={5} md={4} className="text-600">{label}</Col>
    <Col className="text-900">{children || '—'}</Col>
  </Row>
)

const ProviderData = ({ profile }) => (
  <Card className="mb-3">
    <CardHeader className="bg-light">
      <h5 className="mb-0">Datos del registro</h5>
    </CardHeader>
    <CardBody>
      {profile.type === 'company' ? (
        <>
          <DataRow label="Razón social">{profile.legal_name}</DataRow>
          <DataRow label="Nombre comercial">{profile.trade_name}</DataRow>
          <DataRow label="RIF">{formatIdentification(profile.rif, true)}</DataRow>
          <DataRow label="Dirección">{profile.address}</DataRow>
          <DataRow label="Representante legal">
            {[profile.legal_rep_name, formatIdentification(profile.legal_rep_dni), formatPhone(profile.legal_rep_phone)]
              .filter(Boolean)
              .join(' · ')}
          </DataRow>
        </>
      ) : (
        <>
          <DataRow label="Nombre">{`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}</DataRow>
          <DataRow label="Cédula">{formatIdentification(profile.dni)}</DataRow>
          <DataRow label="Ciudad">{[profile.city, profile.state].filter(Boolean).join(', ')}</DataRow>
        </>
      )}
      <DataRow label="Correo electrónico">{profile.email}</DataRow>
      <DataRow label="Teléfono">{formatPhone(profile.phone)}</DataRow>
      <DataRow label="Zonas de cobertura">
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          {profile.coverage_zones.map((zone) => (
            <Badge key={zone} color="soft-primary" pill>{zone}</Badge>
          ))}
        </div>
      </DataRow>
    </CardBody>
  </Card>
)

const VerifiedDocuments = ({ documents }) => (
  <>
    {documents.map((document) => (
      <p key={document.id} className="fs--1 mb-2">
        <FontAwesomeIcon icon={faCheckCircle} className="text-success mr-2" />
        <span className="font-weight-semi-bold">{DOCUMENTS[document.type]?.label || document.type}</span>
        <span className="text-600"> · {document.original_name} · {moment(document.updated_at).format('DD/MM/YYYY')}</span>
      </p>
    ))}
  </>
)

const ProviderProfile = () => {
  const { data, error, isLoading, mutate } = useSWRFetch('marketplace-provider-me', getMyProviderProfile)
  const [files, setFiles] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const profile = data?.data
  const selected = Object.fromEntries(Object.entries(files).filter(([, file]) => file))

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const response = await updateProviderDocuments(selected)
      await mutate(response, false)
      setFiles({})
      setErrors({})
      Toast.fire({
        type: 'success',
        title: profile.status === 'rejected' ? 'Documentos enviados. Revisaremos tu perfil de nuevo.' : 'Documentos actualizados.'
      })
    } catch (requestError) {
      const fieldErrors = requestError?.data?.errors
      if (fieldErrors) {
        setErrors(
          Object.fromEntries(
            Object.entries(fieldErrors).map(([field, messages]) => [field.replace('documents.', ''), [].concat(messages)[0]])
          )
        )
      }
      handleErrorMessage(requestError?.data || requestError)
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  if (error || !profile) {
    return (
      <Alert color="danger">
        No pudimos cargar tu perfil de proveedor.
        <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => mutate()}>
          Reintentar
        </Button>
      </Alert>
    )
  }

  const isVerified = profile.status === 'verified'

  return (
    <>
      <ProviderStatusCard profile={profile} showProfileLink={false} />
      <Row>
        <Col lg={6}>
          <ProviderData profile={profile} />
        </Col>
        <Col lg={6}>
          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h5 className="mb-0">Documentos de verificación</h5>
              <p className="fs--1 text-600 mb-0 mt-1">
                {isVerified
                  ? 'Tus documentos ya fueron aprobados.'
                  : 'Si te pedimos corregir algo, reemplaza aquí el documento. Solo el equipo de Homelan los ve.'}
              </p>
            </CardHeader>
            <CardBody>
              {isVerified ? (
                <VerifiedDocuments documents={profile.documents} />
              ) : (
                <>
                  <ProviderDocumentsFields
                    providerType={profile.type}
                    files={files}
                    errors={errors}
                    currentDocuments={profile.documents}
                    onChange={(type, file) => {
                      setFiles((prev) => ({ ...prev, [type]: file }))
                      setErrors((prev) => ({ ...prev, [type]: null, documents: null }))
                    }}
                  />
                  {errors.documents && <p className="text-danger fs--1">{errors.documents}</p>}
                  <Button color="primary" block disabled={saving || !Object.keys(selected).length} onClick={handleSubmit}>
                    {saving ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faFileUpload} className="mr-1" />
                        Enviar documentos
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ProviderProfile
